import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Smartphone, Mail, Shield, AlertTriangle, CheckCircle, Save, RefreshCw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerificationSettings = () => {
  const [settings, setSettings] = useState({
    mobile_otp_required: true,
    email_otp_required: true,
    allow_skip_verification: false,
    verification_message: "Please verify your mobile and email to continue"
  });
  const [status, setStatus] = useState({
    smtp_configured: false,
    sms_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/verification/settings`);
      if (response.data.success) {
        setSettings(response.data.settings);
        setStatus(response.data.status);
      }
    } catch (error) {
      console.error('Error fetching verification settings:', error);
      toast({
        title: "Error",
        description: "Failed to load verification settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/verification/settings`, settings);
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Verification settings updated successfully"
        });
      }
    } catch (error) {
      console.error('Error saving verification settings:', error);
      toast({
        title: "Error",
        description: "Failed to save verification settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={status.sms_configured ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status.sms_configured ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <Smartphone className={`w-5 h-5 ${status.sms_configured ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <p className="font-medium text-slate-900">SMS Gateway</p>
                <div className="flex items-center gap-2">
                  {status.sms_configured ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">
                        Active: {status.sms_gateway_name || 'Configured'}
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm text-yellow-700">Using Demo Mode (OTP: 123456)</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={status.smtp_configured ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status.smtp_configured ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <Mail className={`w-5 h-5 ${status.smtp_configured ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <p className="font-medium text-slate-900">SMTP Email</p>
                <div className="flex items-center gap-2">
                  {status.smtp_configured ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">Configured</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm text-yellow-700">Not Configured - Email OTP won't work</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <CardTitle>Verification Requirements</CardTitle>
          </div>
          <CardDescription>
            Control which verifications are required for users before they can get predictions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mobile OTP Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Smartphone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <Label className="text-base font-medium">Mobile OTP Verification</Label>
                <p className="text-sm text-slate-500">
                  Require users to verify their mobile number via OTP
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={settings.mobile_otp_required ? "default" : "secondary"}>
                {settings.mobile_otp_required ? "Required" : "Optional"}
              </Badge>
              <Switch
                checked={settings.mobile_otp_required}
                onCheckedChange={(checked) => setSettings({ ...settings, mobile_otp_required: checked })}
                data-testid="mobile-otp-toggle"
              />
            </div>
          </div>

          {/* Email OTP Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <Label className="text-base font-medium">Email OTP Verification</Label>
                <p className="text-sm text-slate-500">
                  Require users to verify their email address via OTP
                </p>
                {!status.smtp_configured && settings.email_otp_required && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ SMTP not configured - Email OTP won't be sent
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={settings.email_otp_required ? "default" : "secondary"}>
                {settings.email_otp_required ? "Required" : "Optional"}
              </Badge>
              <Switch
                checked={settings.email_otp_required}
                onCheckedChange={(checked) => setSettings({ ...settings, email_otp_required: checked })}
                data-testid="email-otp-toggle"
              />
            </div>
          </div>

          {/* Allow Skip Verification */}
          <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <Label className="text-base font-medium">Allow Skip All Verification</Label>
                <p className="text-sm text-slate-500">
                  Users can proceed without any verification (not recommended)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={settings.allow_skip_verification ? "destructive" : "secondary"}>
                {settings.allow_skip_verification ? "Enabled" : "Disabled"}
              </Badge>
              <Switch
                checked={settings.allow_skip_verification}
                onCheckedChange={(checked) => setSettings({ ...settings, allow_skip_verification: checked })}
                data-testid="skip-verification-toggle"
              />
            </div>
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label>Verification Message</Label>
            <Input
              value={settings.verification_message}
              onChange={(e) => setSettings({ ...settings, verification_message: e.target.value })}
              placeholder="Message shown when verification is required"
              data-testid="verification-message-input"
            />
            <p className="text-xs text-slate-500">
              This message is shown to users when they need to verify their details
            </p>
          </div>

          {/* Summary */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2">Current Configuration Summary</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              {settings.allow_skip_verification ? (
                <li>• Users can skip all verification and proceed directly</li>
              ) : (
                <>
                  {settings.mobile_otp_required && <li>• Mobile OTP verification is required</li>}
                  {settings.email_otp_required && <li>• Email OTP verification is required</li>}
                  {!settings.mobile_otp_required && !settings.email_otp_required && (
                    <li>• No verification required (users can proceed directly)</li>
                  )}
                </>
              )}
            </ul>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-verification-settings">
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">How it works</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p>
            <strong>Mobile OTP:</strong> When enabled, users must enter a 6-digit OTP sent to their mobile number.
            Currently using demo mode (OTP: 123456). Configure SMS gateway in Settings → SMS Gateway for live OTP.
          </p>
          <p>
            <strong>Email OTP:</strong> When enabled, users must verify their email. This requires SMTP configuration
            in Settings → SMTP Settings. Without SMTP, email verification won't work.
          </p>
          <p>
            <strong>Skip Verification:</strong> When enabled, users can bypass all verification steps. This is useful
            for testing or when you want maximum conversion, but reduces data quality.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerificationSettings;
